import * as React from "react";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import logo from "../img/logo.png";
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import "../i18n";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ArabicFlag from "../img/Arabic_flag.png";
import FrenchFlag from "../img/French_flag.png";
import EnglishFlag from "../img/English_flag.png";

const LogoImage = styled("img")(() => ({
  width: '120px',
  height: "auto",
  cursor: "pointer",
  display: "block",
}));

const LANGS = [
  { code: "ar", label: "العربية", flagImg: ArabicFlag },
  { code: "fr", label: "French", flagImg: FrenchFlag },
  { code: "en", label: "English", flagImg: EnglishFlag },
];


export default function MenuAppBar() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language || "ar") === "rtl";
  const menuDirection = isRtl ? "rtl" : "ltr";
  const [langAnchorEl, setLangAnchorEl] = React.useState(null);

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleSelectLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("darssy_lang", code);
    handleLangMenuClose();
  };

  const currentLang = LANGS.find((l) => l.code === (i18n.language || "ar")) || LANGS[0];

  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "#ffffff",
            color: "#111827",
            borderBottom: "none",
            boxShadow: "none",
          }}
        >

        <Toolbar
          sx={{
            minHeight: 72,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 2, md: 3 },
          }}
        >
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", mt: '5px', mb: '5px'}}>
            <LogoImage
              src={logo}
              alt="Logo"
              onClick={() => handleNavigate("/")}
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Tooltip title={currentLang.label} arrow>
              <IconButton
                aria-label="language"
                aria-controls="language-menu"
                aria-haspopup="true"
                onClick={handleLangMenuOpen}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  "&:hover": { bgcolor: "#f3f4f6" },
                  border: "1px solid #eef2f7",
                  bgcolor: "#ffffff",
                  overflow: "hidden",
                  p: 1,
                }}
              >
                <Box
                  component="img"
                  src={currentLang.flagImg}
                  alt={currentLang.label}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    imageRendering: "-webkit-optimize-contrast",
                  }}
                />
              </IconButton>

              <Menu
                id="language-menu"
                anchorEl={langAnchorEl}
                open={Boolean(langAnchorEl)}
                onClose={handleLangMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      width: 260,
                      borderRadius: 3,
                      overflow: "hidden",
                      mt: 1,
                      boxShadow: "0 18px 45px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      direction: menuDirection,
                    },
                  },
                }}
                MenuListProps={{ sx: { p: 0 } }}
              >
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f7" }}>
                  <Box sx={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>{t("Language")}</Box>
                  <Box sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>{t("Choose app language")}</Box>
                </Box>

                <Box sx={{ p: 1 }}>
                  {LANGS.map((lng) => {
                    const selected = (i18n.language || "ar") === lng.code;

                    return (
                      <MenuItem
                        key={lng.code}
                        onClick={() => handleSelectLanguage(lng.code)}
                        sx={{
                          borderRadius: 2,
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.15,
                          "&:hover": { bgcolor: "#f9fafb" },
                          bgcolor: selected ? "rgba(79,70,229,0.08)" : "transparent",
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                          <Box
                            component="img"
                            src={lng.flagImg}
                            alt={lng.label}
                            sx={{
                              width: 24,
                              height: 16,
                              borderRadius: "4px",
                              display: "block",
                              objectFit: "contain",
                              imageRendering: "-webkit-optimize-contrast",
                            }}
                          />
                        </ListItemIcon>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ fontSize: 13.5, fontWeight: 800, color: "#111827" }}>{lng.label}</Box>
                          <Box sx={{ fontSize: 12.5, color: "#6b7280", mt: 0.15 }}>{lng.code.toUpperCase()}</Box>
                        </Box>

                        {selected && <Box sx={{ width: 8, height: 8, borderRadius: "999px", bgcolor: "#4f46e5" }} />}
                      </MenuItem>
                    );
                  })}
                </Box>
              </Menu>
            </Tooltip>
            {/* <Button
              variant="text"
              disabled
              sx={{
                color: "#111827",
                fontWeight: 600,
                fontSize: '18px',
                textTransform: "none",
                px: 2.25,
                borderRadius: 2,
                border: '1px solid LightGray',
                "&:hover": {
                  backgroundColor: "#f3f4f6", // neutral-100
                },
              }}
              onClick={() => handleNavigate("/signin")}
            >
              تسجيل الدخول
            </Button> */}

            {/* <Button
              variant="contained"
              disabled
              sx={{
                textTransform: "none",
                fontWeight: 700,
                px: 2.75,
                borderRadius: 2,
                fontSize: '18px',
                backgroundColor: "#000000ff", 
                color: "#fff",
                boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
                "&:hover": { backgroundColor: "#0B57D0" },
                "&:active": { backgroundColor: "#0A4CC0" },
              }}
              onClick={() => handleNavigate("/signup")}
            >
              إنشاء حساب
            </Button> */}
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
